import { FocusMonitor } from '@angular/cdk/a11y';
import { Component, ElementRef, Optional, Self, ViewChild } from '@angular/core';
import { NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Country } from '@app/models/common/country.model';
import { TranslationStorageService } from '@app/services';
import { CountryService } from '@app/services/country.service';
import { debounceTime, tap } from 'rxjs/operators';
import { BaseAutocompleteComponent, Option as BaseOption } from '../base-autocomplete/base-autocomplete.component';

export interface Option extends BaseOption<string> {
    code: string;
}

@Component({
    selector: 'ui-country-autocomplete',
    templateUrl: './country-autocomplete.template.html',
    styleUrls: ['./country-autocomplete.styles.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: CountryAutocompleteComponent }],
    host: {
        '[id]': 'id',
        '[attr.aria-describedby]': 'describedBy',
        '[attr.required]': 'required',
    },
})
export class CountryAutocompleteComponent extends BaseAutocompleteComponent<string> {
    controlType = 'country-autocomplete';
    id = `country-autocomplete-${CountryAutocompleteComponent.nextId++}`;
    countries: Option[];

    /**
     * Set input element dynamically since it isn't available until after the countries have loaded from the API
     */
    @ViewChild('countryInput')
    set inputElement(inputElement: ElementRef<HTMLInputElement> | undefined) {
        this._inputElement = inputElement?.nativeElement ?? null;
    }

    constructor(
        private countryService: CountryService,
        private translationStorage: TranslationStorageService,
        @Optional() @Self() public ngControl: NgControl,
        @Optional() public _parentForm: NgForm,
        public _defaultErrorStateMatcher: ErrorStateMatcher,
        protected elementRef: ElementRef<HTMLElement>,
        protected fm: FocusMonitor
    ) {
        super(ngControl, _parentForm, _defaultErrorStateMatcher, elementRef, fm);
        // Get new results on typing
        const searchControlSub = this.searchControl.valueChanges
            .pipe(
                tap(() => (this.loading = true)),
                debounceTime(500)
            )
            .subscribe((searchTerm: string) => (this.countries ? this._setDisplayOptions(searchTerm) : {}));

        this._subscriptions.push(searchControlSub);
    }

    displayFn = (value: string): string => {
        const option = this.countries.find((opt) => opt.value === value);
        return option ? option.label : value;
    };

    ngOnInit(): void {
        this.loading = true;
        const countryServiceSubscription = this.countryService.allCountries.subscribe((countries: Country[]) => {
            this.countries = countries.map((country: Country) => {
                return {
                    label: this.translationStorage.getLocale() === 'fr-CA' ? country.frenchName : country.name,
                    code: country.code,
                    value: country.name,
                };
            });
            this._setDisplayOptions();
        });
        this._subscriptions.push(countryServiceSubscription);
    }

    getValueOptionFlagCode(): string | null {
        if (this.value === undefined) {
            return;
        }

        return this.options.find((option: Option) => option.value === this.value)?.code;
    }

    protected _setDisplayOptions(searchTerm = ''): void {
        let countries = this.countries;

        if (searchTerm) {
            countries = countries.filter((country) => country.label.toLowerCase().includes(searchTerm.toLowerCase()));
        }

        this.options = countries;

        this.loading = false;
    }
}
